<template>
	<div class="nk-tabs-component-container">
		<div class="nk-tabs-component h-100" tabscope ref="tabs-component">
			<div class="nk-tabs-header-bar" ref="tabsHeaderBar">
				<!-- Sticky Home Tab Button (Icon Only) -->
				<div v-if="homeTab" :id="homeTab.id" :class="{
					'is-active': homeTab.isActive,
					'is-disabled': homeTab.isDisabled
				}" class="nk-tab nk-tab-home-sticky" role="presentation" v-show="homeTab.isVisible">
					<a href="javascript:void(0)" :aria-controls="homeTab.hash" :aria-selected="homeTab.isActive ? true : null" @click.prevent="selectTab(homeTab.hash, $event)" @contextmenu="selectTab(homeTab.hash)" class="nk-tab-pill nk-tab-home-pill" :class="{ 'active': homeTab.isActive, 'disabled': homeTab.isDisabled }" :title="homeTab.header || $t('nav.home')" role="tab">
						<i :class="getTabIcon(homeTab)" class="tab-icon"></i>
					</a>
				</div>

				<!-- Scroller Right Button (toward start in RTL) -->
				<button v-show="canScrollRight" type="button" class="nk-tab-scroll-btn scroll-right" @click="scrollTabs('right')" :title="$t('nav.tabs.scroll-right')" tabindex="-1">
					<i class="fal fa-chevrons-right"></i>
				</button>

				<!-- Scrollable Tabs List -->
				<div class="nk-tabs-scroll-container" ref="tabsScrollContainer" @scroll="onTabsScroll" @wheel="onTabsWheel">
					<div role="tablist" class="nk-tabs-items" tabscope tabindex="1" ref="tabul">
						<div v-for="(tab, i) in scrollableTabs" :key="tab.id || i" :id="tab.id" :class="{
							'is-active': tab.isActive,
							'is-disabled': tab.isDisabled,
							'is-sticky': tab.sticky,
							'has-toolbar': tab.buttons && tab.buttons.length > 0
						}" class="nk-tab" role="presentation" v-show="tab.isVisible" :tabindex="i + 1">
							<a href="javascript:void(0)" :aria-controls="tab.hash" :aria-selected="tab.isActive ? true : null" @click.prevent="selectTab(tab.hash, $event)" @contextmenu="selectTab(tab.hash)" class="nk-tab-pill" :class="{ 'active': tab.isActive, 'disabled': tab.isDisabled }" role="tab">
								<i :class="getTabIcon(tab)" class="tab-icon"></i>
								<span class="tab-title" v-html="tab.header"></span>
								<button v-if="isTabClosable(tab)" type="button" class="nav-close" :title="$t('nav.tabs.cm.close')" @click.stop.prevent="closeTab(tab.hash, tab.isActive === true)" tabindex="-1">
									<i class="fal fa-times"></i>
								</button>
							</a>
						</div>
					</div>
				</div>

				<!-- Scroller Left Button (toward end in RTL) -->
				<button v-show="canScrollLeft" type="button" class="nk-tab-scroll-btn scroll-left" @click="scrollTabs('left')" :title="$t('nav.tabs.scroll-left')" tabindex="-1">
					<i class="fal fa-chevrons-left"></i>
				</button>

				<!-- New Form Button -->
				<button type="button" class="nk-new-form-btn" @click="openNewForm" :title="$t('nav.tabs.new-form')" tabindex="-1">
					<i class="fal fa-plus"></i>
					<span>{{ $t('nav.tabs.new-form') }}</span>
				</button>
			</div>

			<div class="nk-tabs-component-panels" ref="tabs-panel">
				<slot />
			</div>

			<context-menu :target="popperTarget">
				<menu-item v-for="menu in contextMenuItems" :key="menu.title" :title="menu.title" :handler="menu.handler" :shortcut="menu.shortcut"></menu-item>
			</context-menu>
		</div>
	</div>
</template>

<script>
	import $ from 'jquery'
	import _ from 'lodash'
	import TabOrder from '~/statics/taborder-functions'
	import $sc from '~/plugins/shortcut.collection.js'
	import dialog from '~/plugins/nikan-dialog-plugin'
	import { routes } from '~/statics/routes'

	const trim = (str) => (str || '').replace(/^#(.+)$/i, '$1')

	export default {
		props: {
			options: {
				type: Object,
				required: false,
				default: () => ({
					useUrlFragment: true,
				}),
			},
			activeTab: undefined,
			closeConfirm: undefined,
			parentEl: {
				type: Function,
				default: () => { }
			}
		},
		emits: ['changed', 'removed', 'cleared', 'open-navigator'],

		data() {
			return {
				tabs: [],
				activeTabHash: '',
				canScrollLeft: false,
				canScrollRight: false,
				resizeObserver: null,
				scrollRaf: null,
				routes,
				contextMenuItems: [
					{
						title: this.$t('nav.tabs.cm.close'),
						handler: (item, e) => {
							e.preventDefault()

							if (this.currentTab) {
								this.closeTab(
									this.currentTab.hash,
									this.currentTab.isActive === true
								)
							}
						},
						shortcut: $sc.find('close-tab'),
					},
					{
						title: this.$t('nav.tabs.cm.close-other'),
						handler: (item, e) => {
							this.clearTabs(true)
						},
					},
					{
						title: this.$t('nav.tabs.cm.close-all'),
						handler: (item, e) => {
							this.clearTabs(false)
						},
					},
				],
			}
		},

		watch: {
			activeTab: {
				immediate: true,
				deep: true,
				handler: function (value, oldValue) {
					setTimeout(() => {
						if (value !== oldValue) {
							this.selectTab(value)
						}
					}, 1)
				},
			},
			'tabs.length'() {
				this.$nextTick(() => {
					this.updateScrollButtons()
				})
			},
		},

		computed: {
			homeTab() {
				return this.tabs.find((tab) => this.isHomeTab(tab))
			},
			scrollableTabs() {
				return this.tabs.filter((tab) => !this.isHomeTab(tab))
			},
			currentTab() {
				var cachedTabs = [...this.tabs].filter((x) => {
					return x.$options && x.$options.name !== 'ContextMenu'
				})
				return _.head(cachedTabs.filter((x) => x.isActive === true))
			},
		},

		updated() {
			this.defer(this.subscribeKeys, 1)
			this.updateScrollButtons()
		},

		mounted() {
			this.defer(this.subscribeKeys, 1)
			if (this.tabs.length) {
				this.selectTab(this.tabs[0].hash)
			}

			const middleClick = (event) => {
				if (event.which === 2 || event.button === 4) {
					event.preventDefault()
					let tabEl = $(event.target).closest('.nk-tab')
					if (tabEl.length) {
						let hash = tabEl.find('[aria-controls]').attr('aria-controls')
						if (hash) {
							this.closeTab(hash, true)
						}
					}
				}
			}

			$(document).on('click.nkTabHost', (event) => {
				if (event.which === 2 || event.button === 4) {
					event.preventDefault()
				}
			})

			$('.nk-tabs-items').on(
				'click.nkTabHost auxclick.nkTabHost',
				'.nk-tab-pill',
				middleClick.bind(this)
			)

			this.initScrollObserver()
		},

		beforeUnmount() {
			if (this.scrollRaf) {
				cancelAnimationFrame(this.scrollRaf)
			}
			if (this.resizeObserver) {
				this.resizeObserver.disconnect()
				this.resizeObserver = null
			}
			window.removeEventListener('resize', this.updateScrollButtons)
			$(document).off('click.nkTabHost')
			$('.nk-tabs-items').off('click.nkTabHost auxclick.nkTabHost')
		},

		methods: {
			initScrollObserver() {
				window.addEventListener('resize', this.updateScrollButtons)

				if (window.ResizeObserver && this.$refs.tabsScrollContainer) {
					this.resizeObserver = new ResizeObserver(() => {
						this.updateScrollButtons()
					})
					this.resizeObserver.observe(this.$refs.tabsScrollContainer)
					if (this.$refs.tabul) {
						this.resizeObserver.observe(this.$refs.tabul)
					}
				}

				this.$nextTick(() => {
					this.updateScrollButtons()
				})
			},

			formatIconClass(icon) {
				if (!icon) return 'fal fa-file-alt'
				if (
					icon.startsWith('fa-') ||
					icon.startsWith('fas ') ||
					icon.startsWith('fal ') ||
					icon.startsWith('fad ') ||
					icon.startsWith('far ') ||
					icon.startsWith('fa-duotone ') ||
					icon.startsWith('fa-regular ') ||
					icon.startsWith('fa-solid ') ||
					icon.startsWith('fa-light ')
				) {
					return icon
				}
				return `fal fa-${icon}`
			},

			searchRouteById(id) {
				if (!id) return null
				const search = (list) => {
					if (!list || !list.length) return null
					for (let i = 0; i < list.length; i++) {
						const r = list[i]
						if (r.id === id) return r
						if (Array.isArray(r.items) && r.items.length) {
							const found = search(r.items)
							if (found) return found
						}
					}
					return null
				}
				return search(this.routes)
			},

			getTabIcon(tab) {
				if (tab.icon) {
					return this.formatIconClass(tab.icon)
				}
				if (tab.id) {
					const baseId = typeof tab.id === 'string' ? tab.id.split('_')[0] : tab.id
					const route = this.searchRouteById(baseId)
					if (route && route.icon) {
						return this.formatIconClass(route.icon)
					}
				}
				return 'fal fa-file-alt'
			},

			isHomeTab(tab) {
				if (!tab) return false
				const idStr = String(tab.id || '')
				return tab.sticky === true || idStr === 'home' || idStr.startsWith('home_')
			},

			isTabClosable(tab) {
				if (tab.sticky) return false
				if (tab.closable === false) return false
				const idStr = String(tab.id || '')
				if (idStr === 'home' || idStr.startsWith('home_')) return false
				return true
			},

			openNewForm() {
				this.$emit('open-navigator')
				if (this.$parent?.$refs?.navigatorModal && typeof this.$parent.$refs.navigatorModal.open === 'function') {
					this.$parent.$refs.navigatorModal.open()
				}
			},

			onTabsScroll() {
				this.updateScrollButtons()
			},

			onTabsWheel(event) {
				const el = this.$refs.tabsScrollContainer
				if (!el) return

				if (el.scrollWidth > el.clientWidth) {
					event.preventDefault()
					const delta = event.deltaY || event.deltaX
					// In RTL layout, scrolling left moves in the negative direction
					el.scrollLeft -= delta
					this.updateScrollButtons()
				}
			},

			updateScrollButtons() {
				if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf)
				this.scrollRaf = requestAnimationFrame(() => {
					const el = this.$refs.tabsScrollContainer
					if (!el) {
						this.canScrollLeft = false
						this.canScrollRight = false
						return
					}

					const scrollLeft = Math.abs(el.scrollLeft)
					const scrollWidth = el.scrollWidth
					const clientWidth = el.clientWidth
					const maxScroll = Math.max(0, scrollWidth - clientWidth)

					if (maxScroll <= 2) {
						this.canScrollLeft = false
						this.canScrollRight = false
						return
					}

					// In RTL:
					// scrollLeft near 0 is the start (rightmost in RTL) -> canScrollRight is false, canScrollLeft is true
					// scrollLeft near maxScroll is the end (leftmost in RTL) -> canScrollLeft is false, canScrollRight is true
					this.canScrollRight = scrollLeft > 2
					this.canScrollLeft = scrollLeft < maxScroll - 2
				})
			},

			scrollTabs(direction) {
				const el = this.$refs.tabsScrollContainer
				if (!el) return

				const offset = direction === 'left' ? -200 : 200
				el.scrollBy({ left: offset, behavior: 'smooth' })
				setTimeout(() => {
					this.updateScrollButtons()
				}, 220)
			},

			scrollToTab(hash) {
				this.$nextTick(() => {
					const container = this.$refs.tabsScrollContainer
					if (!container) return
					const targetTab = this.findTab(hash)
					if (!targetTab) return

					const tabElement = container.querySelector(`[id='${targetTab.id}'], [aria-controls='${targetTab.hash}']`)
					if (tabElement && typeof tabElement.scrollIntoView === 'function') {
						tabElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
					}
					setTimeout(() => {
						this.updateScrollButtons()
					}, 220)
				})
			},

			cacheHandler(tab, selectedTab) {
				if (!tab.cachable) {
					tab.isActive = tab.hash === selectedTab.hash
					tab.isCached = tab.isActive
					return tab
				}

				if (tab.isCached) {
					tab.isActive = tab.hash === selectedTab.hash
					return tab
				}

				tab.isCached = true
				tab.isActive = tab.hash === selectedTab.hash
				tab.isCached = tab.isActive || tab.isCached
				return tab
			},

			findTab(hash) {
				return this.tabs.find((tab) => {
					return trim(tab.hash) === trim(hash)
				})
			},

			selectTab(selectedTabHash, event) {
				if (event) {
					event.preventDefault()
				}

				const selectedTab = this.findTab(selectedTabHash)

				if (!selectedTab || selectedTab.isDisabled) {
					return
				}

				this.tabs.map((tab) => {
					tab = this.cacheHandler(tab, selectedTab)
				})

				this.activeTabHash = selectedTab.hash
				this.$emit('changed', { tab: selectedTab })

				this.scrollToTab(selectedTab.hash)

				setTimeout(() => {
					const currentTabEl = $(this.$refs['tabs-panel']).find('section:visible')
					if (currentTabEl.length > 0) {
						currentTabEl[0].focus()
						const startupEl = currentTabEl.find('[startup]:visible')
						if (startupEl.length > 0) {
							startupEl[0].focus()
						}
					}
				}, 50)
				this.$router.push({ name: 'page', params: { page: selectedTab.id, ...selectedTab.params }, query: { ...selectedTab.query, ...selectedTab.$props.query } })
			},

			setTabVisible(hash, visible) {
				const tab = this.findTab(hash)

				if (!tab) {
					return
				}

				tab.isVisible = visible

				if (tab.isActive) {
					tab.isActive = visible

					this.tabs.every((tab, index, array) => {
						if (tab.isVisible) {
							tab.isActive = true
							return false
						}
						return true
					})
				}
			},

			emitRemove(i) {
				this.$emit('removed', i)
			},

			closeTab(selectedTabHash, selectPrevTab) {
				const selectedTab = this.findTab(selectedTabHash)
				var newSelectedTab = null
				const remove = (tab, i) => {
					if (tab.isActive === true) {
						if (i > 0) {
							newSelectedTab = cachedTabs[i - 1]
						} else if (i === 0 && this.tabs.length > 1) {
							newSelectedTab = cachedTabs[i + 1]
						}
					}

					tab.isActive = false
					this.emitRemove(i)

					if (selectPrevTab && newSelectedTab != null) {
						this.selectTab(newSelectedTab.hash)
					}
					this.tabs.splice(i, 1)
					this.$nextTick(() => {
						this.updateScrollButtons()
					})
				}

				if (!selectedTab || selectedTab.isDisabled || selectedTab.sticky) {
					return
				}

				var cachedTabs = [...this.tabs].filter((x) => {
					return x.$options && x.$options.name !== 'ContextMenu'
				})
				const find = (tab) => {
					if (!this.$parent.$refs.tabContent || !this.$parent.$refs.tabContent.length)
						return null
					for (let tabContentRef of this.$parent.$refs.tabContent) {
						if ($(tabContentRef.$el).closest(`[id='${tab.hash}']`).length > 0)
							return tabContentRef
					}
					return null
				}
				for (var i = 0; i < cachedTabs.length; i++) {
					var tab = cachedTabs[i]

					if (trim(tab.hash) === trim(selectedTab.hash)) {
						let childClass = find(tab)
						var isClosable = tab.closable !== false && tab.$slots.default?.()?.[0]?.props?.closable !== false
						if (childClass && isClosable && typeof childClass.isDataChanged === 'function') {
							childClass
								.isDataChanged()
								.then((isChanged) => {
									if (isChanged) {
										if (typeof childClass.changedFormAlert === 'function') {
											childClass.changedFormAlert((obj) => { remove(obj.tab, obj.i) }, { tab, i })
										} else
											dialog({
												title: this.$t('notifications.titles-attention'),
												body: this.$t('nav.tabs.unsaved-changed-data'),
												autoClose: true,
												buttons: [
													{
														title: this.$t('dialog.yes'),
														shortcut: $sc.find('yes', 'dialog'),
														isPrimary: true,
														handle: (button, page, e) => {
															remove(tab, i)
														},
													},
													{
														title: this.$t('dialog.cancel'),
														shortcut: $sc.find('cancel', 'dialog'),
														isCancel: true,
														handle: (button, page, e) => { },
													},
												],
											})
									} else {
										remove(tab, i)
									}
								})
								.catch((e) => {
									remove(tab, i)
								})
						} else {
							remove(tab, i)
						}
						break
					}
				}
			},

			clearTabs(keepCurrent) {
				let _tabs = [...this.tabs].filter(
					(x) => !x.$options || x.$options.name !== 'ContextMenu'
				)
				let tabsToKeep = _tabs.filter(
					(x) =>
						x.sticky === true ||
						(keepCurrent && trim(x.hash) === trim(this.currentTab.hash))
				)
				this.$emit('cleared', tabsToKeep)

				if (tabsToKeep.length > 0) {
					let newSelectedTab = tabsToKeep[tabsToKeep.length - 1]
					this.selectTab(newSelectedTab.hash)

					for (var i = this.tabs.length - 1; i >= 0; i--) {
						if (tabsToKeep.findIndex((x) => x.id === this.tabs[i].id) === -1) {
							this.tabs.splice(i, 1)
						}
					}
					this.$nextTick(() => {
						this.updateScrollButtons()
					})
				}
			},

			popperTarget() {
				return this.$refs.tabsHeaderBar || $('.nk-tabs-header-bar')[0]
			},

			subscribeKeys() {
				this.$keyHandler.ReserveAt('tab-taborder-ctrl-enter-tabscope', 0)
				this.$keyHandler.ReserveAt('tab-taborder-previous', 0)
				this.$keyHandler.ReserveAt('tab-taborder-next', 0)

				TabOrder.subscribeKeys('tab', this)
				this.$keyHandler.Subscribe({
					name: `arrowLeft-button-asd65g`,
					...$sc.find('ctrl-arrow-left'),
					el: this.parentEl(),
					context: this,
					handler: function (sender, e) {
						e.preventDefault()
						let findTab = this.tabs.findIndex(x => x.hash == this.activeTabHash)

						if (findTab + 1 <= this.tabs.length - 1) {
							this.selectTab(this.tabs[findTab + 1]?.hash, e)
						} else {
							this.selectTab(this.tabs[0]?.hash, e)
						}
					},
				})
				this.$keyHandler.Subscribe({
					name: `arrowRight-button-asd65h`,
					...$sc.find('ctrl-arrow-right'),
					el: this.parentEl(),
					context: this,
					handler: function (sender, e) {
						e.preventDefault()
						let findTab = this.tabs.findIndex(x => x.hash == this.activeTabHash)

						if (findTab - 1 >= 0) {
							this.selectTab(this.tabs[findTab - 1]?.hash, e)
						} else {
							this.selectTab(this.tabs[this.tabs.length - 1]?.hash, e)
						}
					},
				})

				this.$keyHandler.Subscribe({
					name: `tab-open-close`,
					shortcut: this.contextMenuItems[0].shortcut,
					el: this.$refs['tabs-component'],
					context: this,
					handler: function (sender, e) {
						e.preventDefault()
						this.contextMenuItems[0].handler.call(this, {}, e)
					},
				})
			},
		},
	}
</script>

<style>
	.context-menu ul li .cm-item .cm-shortcut {
		color: #949494;
	}
</style>
